import type { ReactNode } from 'react';
import LandingHeroBackground from './LandingHeroBackground';

/**
 * Full-viewport stagnant hero background (particles, mesh, ribbons, etc.) for public/marketing routes.
 * Pairs with {@link PublicBandSection}: odd bands stay transparent over the canvas; even bands use a frosted slate band.
 *
 * Each route mount creates one `LandingHeroBackground` instance (one canvas). Navigating away unmounts it — not a single shared global canvas.
 */
export function PublicMarketingLayout({ children }: { children: ReactNode }) {
  return (
    <div className="relative min-h-screen bg-[#05050d] font-sans text-white">
      <div className="pointer-events-none fixed inset-0 z-0">
        <LandingHeroBackground appearance="dark" />
      </div>
      <div className="relative z-10">{children}</div>
    </div>
  );
}

type PublicBandSectionProps = {
  /** 1-based index: odd → transparent (background shows); even → frosted band (same as landing “even” sections). */
  band: number;
  className?: string;
  children: ReactNode;
  id?: string;
};

export function PublicBandSection({ band, className = '', children, id }: PublicBandSectionProps) {
  const isEven = band % 2 === 0;
  return (
    <section
      id={id}
      className={`relative z-10 overflow-hidden ${
        isEven ? 'border-y border-white/5 bg-[#0a0f1e]/80 backdrop-blur-sm' : ''
      } ${className}`.trim()}
    >
      {children}
    </section>
  );
}
